import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Link2, FileText, SlidersHorizontal, BarChart3 } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import { usePerms } from '../permissions/PermissionsContext';

// Logo URLs — MUST match the main Dashboard.js platform cards (same logos/colors)
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';
const LOGO_CLOUDGM = '/brand/ast-logo-cloudgm.png';
const LOGO_FITNESS = '/brand/ast-logo-606-fitness.png';
const LOGO_CGMCOIN = '/brand/ast-logo-cgm-coin.png';
const LOGO_COMMUNITY = '/brand/ast-logo-606-community.png';
const LOGO_MEDIADOCS = '/brand/ast-logo-606-mediadocs.png';

export default function DashboardFinance() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages, canViewRoute } = usePerms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Platform cards - matching Dashboard.js style with logos
  const platforms = [
    {
      id: 'cloudgm',
      nameAr: 'CLOUDGM FZ LLC',
      nameEn: 'CLOUDGM FZ LLC',
      descAr: 'الإدارة المالية لمنصة كلاود جي إم',
      descEn: 'CloudGM Financial Management',
      logo: LOGO_CLOUDGM,
      color: '#1E9BD9',
      route: '/finance/cloudgm',
      screenCode: 'FIN-P01',
      comingSoon: false,
    },
    {
      id: 'fitness',
      nameAr: '606 FITNESS',
      nameEn: '606 FITNESS',
      descAr: 'الإدارة المالية لمنصة اللياقة',
      descEn: '606 Fitness Financial Management',
      logo: LOGO_FITNESS,
      color: '#10b981',
      route: '/dashboard/finance/platform/fitness',
      screenCode: 'FIN-P02',
      comingSoon: false,
    },
    {
      id: 'cgm-coin',
      nameAr: 'CGM COIN',
      nameEn: 'CGM COIN',
      descAr: 'الإدارة المالية للعملة الرقمية',
      descEn: 'CGM Coin Financial Management',
      logo: LOGO_CGMCOIN,
      color: '#d4af37',
      route: '/dashboard/finance/platform/cgm_coin',
      screenCode: 'FIN-P03',
      comingSoon: false,
    },
    {
      id: 'community',
      nameAr: '606 COMMUNITY',
      nameEn: '606 COMMUNITY',
      descAr: 'الإدارة المالية للمجتمع',
      descEn: 'Community Financial Management',
      logo: LOGO_COMMUNITY,
      color: '#FF6B00',
      route: '/dashboard/finance/platform/community',
      screenCode: 'FIN-P04',
      comingSoon: false,
    },
    {
      id: 'mediadocs',
      nameAr: '606 MEDIADOCS',
      nameEn: '606 MEDIADOCS',
      descAr: 'الإدارة المالية للميديا',
      descEn: 'MediaDocs Financial Management',
      logo: LOGO_MEDIADOCS,
      color: '#F5E6D3',
      route: '/dashboard/finance/platform/mediadocs',
      screenCode: 'FIN-P05',
      comingSoon: false,
    },
    {
      id: 'asb-admin',
      nameAr: 'ASB606 ADMIN',
      nameEn: 'ASB606 ADMIN',
      descAr: 'الإدارة المالية للنظام',
      descEn: 'Admin Financial Management',
      logo: LOGO_TRIANGLE,
      color: '#d4af37',
      route: '/finance/asb606',
      screenCode: 'FIN-P06',
      comingSoon: false,
    },
    {
      id: 'tax-report',
      nameAr: 'الضريبة المضافة',
      nameEn: 'VAT',
      descAr: 'التحكم بالضريبة والتقارير الضريبية',
      descEn: 'Tax Control & Tax Reports',
      logo: null,
      icon: FileText,
      color: '#EF4444',
      route: '/dashboard/finance/vat',
      screenCode: 'FIN-P07',
      comingSoon: false,
    },
    {
      id: 'finance-settings',
      nameAr: 'إعدادات القسم المالي',
      nameEn: 'FINANCE SETTINGS',
      descAr: 'العمولات، التحكم بالمزودين، والإعدادات المالية',
      descEn: 'Commissions, provider controls, financial settings',
      logo: null,
      icon: SlidersHorizontal,
      color: '#8B5CF6',
      route: '/dashboard/finance/settings',
      screenCode: 'FIN-P09',
      comingSoon: false,
    },
    {
      // iter-218m (Feb 2026) — Aggregated finance stats (salaries + company payments)
      id: 'financial-statistics',
      nameAr: 'الإحصائيات المالية',
      nameEn: 'FINANCIAL STATISTICS',
      descAr: 'مجموع الرواتب ومدفوعات الشركة في مكان واحد',
      descEn: 'Total salaries + company payments at a glance',
      logo: null,
      icon: BarChart3,
      color: '#22D3EE',
      route: '/dashboard/finance/statistics',
      screenCode: 'FIN-P10',
      comingSoon: false,
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      {/* Background Glow Effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#d4af37]/3 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <DashboardHeader 
        showBackButton={true}
        backPath="/dashboard"
      />

      {/* Breadcrumb with line */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate('/dashboard')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
              {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
            </button>
            <span className="text-[#71717A]">{'<'}</span>
            <span className="text-[#d4af37]">
              {language === 'ar' ? 'المالية' : 'Finance'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {language === 'ar' ? 'المالية' : 'Finance'}
            </h1>
            <p className="text-[#71717A] text-lg">
              {language === 'ar' ? 'اختر المنصة' : 'Select Platform'}
            </p>
          </div>

          {/* Platforms Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {visiblePages(platforms, 'route').map((platform) => (
              <motion.div
                key={platform.id}
                variants={cardVariants}
                whileHover={!platform.comingSoon ? { y: -8, scale: 1.02 } : {}}
                onClick={() => {
                  if (platform.comingSoon) {
                    toast.info(language === 'ar' ? 'قريباً' : 'Coming Soon');
                  } else {
                    navigate(platform.route);
                  }
                }}
                className={`group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all duration-300 text-center ${
                  platform.comingSoon 
                    ? 'border-white/5 opacity-60 cursor-not-allowed' 
                    : 'border-white/10 hover:border-[#d4af37]/50 cursor-pointer hover:shadow-lg hover:shadow-[#d4af37]/10'
                }`}
                data-testid={`finance-platform-${platform.id}`}
              >
                {/* Screen Code Badge */}
                <div className="absolute top-3 left-3 px-2 py-0.5 bg-white/5 rounded-md">
                  <span className="text-[10px] font-mono text-[#71717A]">{platform.screenCode}</span>
                </div>

                {/* Coming Soon Badge */}
                {platform.comingSoon && (
                  <div className="absolute top-3 right-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                    <span className="text-[10px] font-medium text-amber-400">
                      {language === 'ar' ? 'قريباً' : 'Soon'}
                    </span>
                  </div>
                )}

                {/* Icon/Logo */}
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300"
                  style={{ 
                    backgroundColor: platform.comingSoon ? 'rgba(255,255,255,0.03)' : `${platform.color}15`,
                    border: `1px solid ${platform.comingSoon ? 'rgba(255,255,255,0.05)' : `${platform.color}30`}`
                  }}
                >
                  {platform.logo ? (
                    <img 
                      src={platform.logo} 
                      alt={platform.nameEn} 
                      className="w-10 h-10 object-contain"
                    />
                  ) : platform.icon ? (
                    <platform.icon 
                      className="h-8 w-8" 
                      style={{ color: platform.comingSoon ? '#71717A' : platform.color }} 
                    />
                  ) : null}
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-white mb-2" style={{ direction: 'ltr' }}>
                  {platform.nameEn}
                </h3>

                {/* Description */}
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? platform.descAr : platform.descEn}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      {/* Footer Logo */}
      <div className="flex justify-center pb-12">
        <img 
          src={LOGO_TRIANGLE} 
          alt="ASB606" 
          className="w-16 h-16 object-contain opacity-20"
        />
      </div>
    </div>
  );
}
